import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Building2, CircleDot, GraduationCap, UserPlus, UserX, Users } from "lucide-react";
import type { CircleListResponse, OverviewResponse } from "@mubin/contracts";
import { NeedsAction } from "../components/inbox";
import { Empty, Loading } from "../components/ui";
import { api } from "../lib/api";
import { genderLabel, roleLabel } from "../lib/labels";
import { useMe } from "../lib/session";

export function HomePage() {
  const { data: me } = useMe();
  const overview = useQuery({
    queryKey: ["overview", me?.activeRole],
    queryFn: () => api<OverviewResponse>("/v1/admin/overview"),
  });
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, "all"],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
  });
  const o = overview.data;

  const greeting = new Date().getHours() < 12 ? "صباح الخير" : "مساء الخير";
  const firstName = firstWord(me?.user.name ?? "");

  return (
    <>
      <section className="welcome" aria-label="ترحيب">
        <div className="welcome-text">
          <p className="welcome-kicker">{greeting}</p>
          <h1>{firstName ? `أهلًا، ${firstName}` : "أهلًا بك"}</h1>
          <p className="welcome-sub">
            {me?.activeRole ? `تعمل الآن بصفة ${roleLabel[me.activeRole]}` : ""}
            {o ? ` · ${o.circles} حلقة و${o.students} طالبًا ضمن نطاقك` : ""}
          </p>
        </div>
      </section>
      <div className="stats" aria-busy={overview.isPending}>
        <Stat
          to="/join-requests"
          icon={<UserPlus size={20} strokeWidth={1.75} />}
          label="طلبات انضمام جديدة"
          value={o?.pendingJoinRequests}
          attention={!!o && o.pendingJoinRequests > 0}
        />
        {o && o.pendingDeletionRequests > 0 && (
          <Stat
            to="/deletion-requests"
            icon={<UserX size={20} strokeWidth={1.75} />}
            label="طلبات حذف حساب"
            value={o.pendingDeletionRequests}
            attention
          />
        )}
        <Stat
          to="/branches"
          icon={<Building2 size={20} strokeWidth={1.75} />}
          label="المجمعات"
          value={o?.branches}
        />
        <Stat
          to="/circles"
          icon={<CircleDot size={20} strokeWidth={1.75} />}
          label="الحلقات"
          value={o?.circles}
        />
        <Stat
          to="/users"
          search={{ tab: "student" }}
          icon={<GraduationCap size={20} strokeWidth={1.75} />}
          label="الطلاب المسجلون"
          value={o?.students}
        />
        <Stat
          to="/users"
          search={{ tab: "teacher" }}
          icon={<Users size={20} strokeWidth={1.75} />}
          label="المعلمون المسندون"
          value={o?.teachers}
        />
      </div>

      <NeedsAction />

      <div className="home-grid">
        {circles.data && circles.data.circles.length > 0 && <CircleLoad circles={circles.data.circles} />}
        <section className="card" aria-labelledby="circles-h">
          <div className="card-h">
            <h2 id="circles-h">الحلقات ضمن نطاقك</h2>
            {circles.data && <span className="num">{circles.data.circles.length} حلقة</span>}
          </div>
          {circles.isPending ? (
            <Loading />
          ) : circles.isError ? (
            <Empty title="تعذر تحميل الحلقات">{circles.error.message}</Empty>
          ) : circles.data.circles.length === 0 ? (
            <Empty title="لا توجد حلقات في نطاقك بعد">تظهر الحلقات هنا بعد إنشائها من صفحة الحلقات.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الحلقة</th>
                    <th>المجمع</th>
                    <th>المعلم</th>
                    <th>الطلاب</th>
                  </tr>
                </thead>
                <tbody>
                  {circles.data.circles.slice(0, 8).map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to="/circles/$circleId" params={{ circleId: c.id }}>
                          {c.name}
                        </Link>
                        {c.gender && <span className="muted small"> · {genderLabel[c.gender]}</span>}
                      </td>
                      <td>{c.branchName}</td>
                      <td>{c.teachers.map((t) => t.name).join("، ") || <span className="muted">—</span>}</td>
                      <td className="num">
                        {c.studentCount}
                        {c.capacity ? <span className="muted"> / {c.capacity}</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function Stat({
  to,
  search,
  icon,
  label,
  value,
  attention = false,
}: {
  to: "/join-requests" | "/deletion-requests" | "/branches" | "/circles" | "/users";
  search?: { tab: "student" | "teacher" };
  icon: ReactNode;
  label: string;
  value: number | undefined;
  attention?: boolean;
}) {
  return (
    <Link to={to} search={search as never} className={attention ? "stat attention" : "stat"}>
      <span className="stat-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="stat-body">
        <span className="v num">{value ?? "—"}</span>
        <span className="k">{label}</span>
      </span>
    </Link>
  );
}

/** Students per circle, the busiest first: a simple bar per circle against the largest. */
function CircleLoad({ circles }: { circles: CircleListResponse["circles"] }) {
  const rows = [...circles].sort((a, b) => b.studentCount - a.studentCount).slice(0, 8);
  const max = Math.max(1, ...rows.map((c) => c.capacity ?? c.studentCount));
  return (
    <section className="card" aria-labelledby="load-h">
      <div className="card-h">
        <h2 id="load-h">الطلاب في الحلقات</h2>
        <span>الأكثر طلابًا</span>
      </div>
      {rows.length === 0 ? (
        <Empty title="لا توجد حلقات بعد" />
      ) : (
        <ul className="bars">
          {rows.map((c) => (
            <li key={c.id}>
              <span className="bar-label">{c.name}</span>
              <span className="bar-track" aria-hidden="true">
                {c.capacity ? (
                  <span className="bar-cap" style={{ width: `${(c.capacity / max) * 100}%` }} />
                ) : null}
                <span className="bar-fill" style={{ width: `${(c.studentCount / max) * 100}%` }} />
              </span>
              <span className="bar-value num">
                {c.studentCount}
                {c.capacity ? <span className="muted"> / {c.capacity}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The first word of the name, skipping honorifics such as «الشيخ». */
function firstWord(name: string): string {
  const honorifics = new Set(["الشيخ", "الأستاذ", "الأستاذة", "د.", "الدكتور"]);
  return (
    name
      .trim()
      .split(/\s+/)
      .find((w) => !honorifics.has(w)) ?? ""
  );
}
